import React from "react";
import TeamPageNav from "../../components/TeamPageNav/TeamPageNav";
import TeamMemberCard from "../../components/TeamMemberCard/TeamMemberCard";
import CindyBauer from "../../assets/team-members/cindy-bauer.jpg";
import SamBeger from "../../assets/team-members/sam-beger.jpg";
import BentleyBobrow from "../../assets/team-members/bentley-bobrow.jpg";
import MichaelChristopher from "../../assets/team-members/michael-christopher.jpg";
import LynnGerald from "../../assets/team-members/lynn-gerald.png";
import AshleyLowe from "../../assets/team-members/ashley-lowe.jpg";
import BenWright from "../../assets/team-members/ben-wright.jpg";
import { siteDarkGreen } from "../../providers/Theme";
import {
  PageSection,
  PageInner,
  PageHeading,
  PageSubtitle,
  MembersGrid,
  BackButton
} from "./styled.components";

interface Member {
  name: string;
  specialty: string;
  org: string;
  highlights: string[];
  bio: string;
  photo?: string;
}

const members: Member[] = [
  {
    name: "Cindy Salm Bauer, MD, FAAAAI",
    specialty: "Allergy & Immunology",
    org: "Division Chief & Medical Director, Phoenix Children's Hospital",
    photo: CindyBauer,
    highlights: [
      "Division Chief and Medical Director, Allergy and Immunology, Phoenix Children's",
      "Co-Director, Eosinophilic Gastrointestinal Disease Clinic",
      "Associate Professor, Clinical Scholar track, Department of Child Health, University of Arizona",
      "Associate Professor, Creighton University School of Medicine",
      "Assistant Professor & Fellowship Site Director, Mayo Clinic Arizona"
    ],
    bio: "Dr. Bauer founded the Phoenix Children's Allergy and Immunology Clinic in 2013, focusing on food allergy, eosinophilic esophagitis, asthma, and atopic dermatitis. She participated in multi-center NIH-funded studies, serves as Medical Director of Camp-Not-A-Wheeze, and is a past President of the Arizona Allergy and Asthma Society."
  },
  {
    name: "Sam Beger, MD, MPH",
    specialty: "Emergency Medicine & Public Health",
    org: "Board-Certified Emergency Medicine Physician",
    photo: SamBeger,
    highlights: [],
    bio: "Dr. Beger is a board-certified Emergency Medicine physician and Arizona native whose work bridges frontline care, patient education, and public health. As co-founder of Tether Supervision, he has helped train hundreds of people in epinephrine auto-injector use and emergency response protocols, supporting KRF's mission to help schools and communities respond quickly to food allergy emergencies."
  },
  {
    name: "Bentley J. Bobrow, MD, FACEP, FAHA",
    specialty: "Emergency Medicine",
    org: "Department of Emergency Medicine, University of Arizona",
    photo: BentleyBobrow,
    highlights: [
      "Department of Emergency Medicine, University of Arizona College of Medicine, Tucson",
      "Co-Director of EMS Research",
      "Associate Director, Arizona Emergency Medicine Research Center"
    ],
    bio: "Dr. Bobrow is a nationally recognized emergency medicine physician and researcher specializing in out-of-hospital emergencies and EMS systems. His expertise in emergency response directly informs KRF's school and community preparedness training programs."
  },
  {
    name: "Michael Christopher, MD, FACEP",
    specialty: "Emergency Medicine",
    org: "Attending Emergency Physician, St. Joseph's Hospital",
    photo: MichaelChristopher,
    highlights: [
      "Attending Emergency Physician, St. Joseph's Hospital and Medical Center",
      "Clinical Professor of Emergency Medicine, Creighton University School of Medicine, Phoenix"
    ],
    bio: "Dr. Christopher is an attending emergency physician at St. Joseph's Hospital and Clinical Professor of Emergency Medicine at Creighton University in Phoenix. His frontline expertise strengthens KRF's mission to ensure schools and communities can respond to food allergy emergencies."
  },
  {
    name: "Lynn B. Gerald, PhD, MSPH",
    specialty: "Pediatric Asthma & Respiratory Health",
    org: "NHLBI-Funded Researcher, University of Arizona",
    photo: LynnGerald,
    highlights: [
      "20+ years continuous NHLBI funding in school-based asthma research",
      "Co-Chair, American Thoracic Society policy statement on school inhaler access"
    ],
    bio: "Dr. Gerald has maintained continuous NHLBI funding in school-based asthma research for 20 years and was instrumental in passing Arizona's stock inhaler legislation. She works with states nationwide on implementation and has partnered with KRF since its founding."
  },
  {
    name: "Ashley A. Lowe, PhD, MSPH",
    specialty: "Public Health & School Health",
    org: "Assistant Professor, University of Arizona College of Nursing",
    photo: AshleyLowe,
    highlights: [
      "Assistant Professor, University of Arizona College of Nursing",
      "Program Director, Stock Inhaler for Schools Program",
      "Research Scientist, Asthma & Airway Disease Research Center"
    ],
    bio: "Dr. Lowe leads a statewide initiative ensuring rescue medications are accessible across more than 1,000 Arizona schools. Over eight years, she has trained thousands of school nurses and staff to respond to emergencies, significantly reducing emergency transports for students."
  },
  {
    name: "Benjamin Wright, MD",
    specialty: "Allergy & Clinical Immunology",
    org: "Consultant, Allergy & Immunology, Mayo Clinic Arizona",
    photo: BenWright,
    highlights: [
      "Consultant, Allergy, Asthma & Clinical Immunology, Mayo Clinic Arizona",
      "Physician, Division of Allergy & Immunology, Phoenix Children's",
      "Chair, Eosinophilic GI Disorders Subcommittee, AAAAI"
    ],
    bio: "Dr. Wright holds dual appointments at Mayo Clinic Arizona and Phoenix Children's, with clinical and research focus in allergy and immunology. He is Site Principal Investigator for CEGIR and chairs the AAAAI's Eosinophilic Gastrointestinal Disorders Subcommittee."
  }
];

const MedicalAdvisoryCouncil: React.FC = () => (
  <>
    <TeamPageNav />
    <PageSection>
      <PageInner>
        <PageHeading $color={siteDarkGreen}>
          Medical Advisory Council
        </PageHeading>
        <PageSubtitle>
          Our courses and training programs are built on a foundation of
          clinical expertise. KRF's Medical Advisory Council — composed of
          leading physicians and researchers in allergy, immunology, emergency
          medicine, and public health — ensures everything we teach is
          evidence-based and ready for real-world response.
        </PageSubtitle>
        <MembersGrid>
          {members.map(m => (
            <TeamMemberCard
              key={m.name}
              photo={m.photo}
              name={m.name}
              role={m.specialty}
              roleColor={siteDarkGreen}
              subtitle={m.org}
              highlights={m.highlights}
              bio={m.bio}
            />
          ))}
        </MembersGrid>
        <BackButton
          to="/about-us/our-team"
          onClick={() => window.scrollTo(0, 0)}
        >
          Back to Our Team →
        </BackButton>
      </PageInner>
    </PageSection>
  </>
);

export default MedicalAdvisoryCouncil;
